"use client"

import type React from "react"

import { useState, useRef, useEffect } from "react"
import type { Message } from "@/types"
import { Send, Settings } from "lucide-react"
import Image from "next/image"
import heartPic from "@/public/heart.svg"

import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import remarkBreaks from 'remark-breaks'

interface ChatAreaProps {
  messages: Message[]
  sendMessage: (content: string) => Promise<void>
  isLoading: boolean
  openSettings: () => void
}

export default function ChatArea({ messages, sendMessage, isLoading, openSettings }: ChatAreaProps) {
  const [input, setInput] = useState("")
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!input.trim() || isLoading) return

    const message = input
    setInput("")
    await sendMessage(message)
  }

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [messages])

  return (
    <div className="flex-1 flex flex-col h-full bg-white">
      <div className="p-4 border-b border-[#e6e4e3] flex justify-between items-center">
        <h1 className="text-xl font-medium text-[#4a4a4a]">声生不息AI智能诊疗室</h1>
        <button
          onClick={openSettings}
          className="p-2 rounded-full hover:bg-[#f5f5f5] transition-colors"
          aria-label="Settings"
        >
          <Settings size={20} className="text-[#6b6b6b]" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 ? (
          <div className="h-full flex items-center justify-center text-[#9a9a9a]">
            <p>开始和智能诊疗系统对话吧！</p>
          </div>
        ) : (
          messages.map((message) => (
            <div key={message.id} className={`flex mb-4 ${message.role === "user" ? "justify-end" : "justify-start"}`}>
              <div
                // Added 'prose' class for basic Tailwind typography styling (optional)
                // Removed whitespace-pre-wrap as ReactMarkdown handles block formatting
                className={`max-w-[80%] p-3 rounded-2xl prose prose-sm sm:prose-base ${
                  message.role === "user" ? "bg-[#d8e8dc] text-[#3a3a3a]" : "bg-[#e6e4e3] text-[#4a4a4a]"
                }`}
              >
                {/* --- Use ReactMarkdown here --- */}
                <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
                  {message.content}
                </ReactMarkdown>
                {/* --- End of change --- */}
              </div>
            </div>
          ))
        )}
        {isLoading && (
          <div className="flex justify-start">
            <div className="max-w-[80%] p-3 rounded-2xl bg-[#e6e4e3] text-[#4a4a4a]">
              <div className="flex space-x-2">
                <div
                  className="w-2 h-2 rounded-full bg-[#9a9a9a] animate-bounce"
                  style={{ animationDelay: "0ms" }}
                ></div>
                <div
                  className="w-2 h-2 rounded-full bg-[#9a9a9a] animate-bounce"
                  style={{ animationDelay: "150ms" }}
                ></div>
                <div
                  className="w-2 h-2 rounded-full bg-[#9a9a9a] animate-bounce"
                  style={{ animationDelay: "300ms" }}
                ></div>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="p-4 border-t border-[#e6e4e3] relative">
        <div className="absolute -top-47.5 right-20">
          <Image src={heartPic} alt="Logo" width={250} />
        </div>
        <form onSubmit={handleSubmit} className="flex items-center gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Type your message..."
            className="flex-1 p-3 rounded-xl border border-[#e6e4e3] focus:outline-hidden focus:ring-2 focus:ring-[#d8e8dc]"
            disabled={isLoading}
          />
          <button
            type="submit"
            disabled={!input.trim() || isLoading}
            className="p-3 rounded-xl bg-[#ffcd00] text-[#ffffff] disabled:opacity-75 hover:opacity-50 transition-colors"
          >
            <Send size={20} />
          </button>
        </form>
      </div>
    </div>
  )
}
